import { OrganizationNodeProps } from '@ixicare/resources';

export interface OrgTreeNode {
  id: string;
  name: string;
  children: OrgTreeNode[];
}

// The organizations as the selector shows them: the API tree, sorted by name at
// every level. Sub-organizations regularly share a name with a sibling under
// another customer, so the selector keeps the nesting rather than flattening to
// a list where those are the same row twice.
export function buildOrgTree(roots: OrganizationNodeProps[]): OrgTreeNode[] {
  return [...roots]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((node) => ({ id: node.id, name: node.name, children: buildOrgTree(node.children) }));
}

// Keeps a match and everything under it, plus the ancestors it hangs from — so
// searching a sub-organization three levels down still shows which customer it
// belongs to, instead of a bare name with no context.
export function filterOrgTree(nodes: OrgTreeNode[], needle: string, parentMatched = false): OrgTreeNode[] {
  const result: OrgTreeNode[] = [];
  for (const node of nodes) {
    const selfMatches = parentMatched || node.name.toLowerCase().includes(needle);
    const children = filterOrgTree(node.children, needle, selfMatches);
    if (!selfMatches && children.length === 0) continue;
    result.push({ ...node, children });
  }
  return result;
}

export function countOrgs(nodes: OrgTreeNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countOrgs(node.children), 0);
}

export function findOrgNode(roots: OrganizationNodeProps[], id: string): OrganizationNodeProps | undefined {
  for (const root of roots) {
    if (root.id === id) return root;
    const found = findOrgNode(root.children, id);
    if (found) return found;
  }
  return undefined;
}

// Sentinel selector value for the synthetic "no organization" bucket. Only
// offered to unscoped (Watcherr) employees; org admins never see non-org sites.
export const NO_ORG_VALUE = '__no_org__';

export type BillingRowScope = { organizationUuid: string } | { siteUuid: string };

export interface BillingNodeRow {
  key: string;
  kind: 'org' | 'site';
  name: string;
  depth: number;
  scope: BillingRowScope;
}

// Depth-first walk producing the table rows: an org row (subtree total) followed
// by its direct site rows, then recursively each sub-org and its sites.
export function flattenBillingRows(node: OrganizationNodeProps, depth = 0): BillingNodeRow[] {
  const rows: BillingNodeRow[] = [
    { key: `org-${node.id}`, kind: 'org', name: node.name, depth, scope: { organizationUuid: node.id } },
  ];
  [...node.sites]
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((site) => {
      rows.push({
        key: `site-${site.uuid}`,
        kind: 'site',
        name: site.name,
        depth: depth + 1,
        scope: { siteUuid: site.uuid },
      });
    });
  [...node.children]
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((child) => {
      rows.push(...flattenBillingRows(child, depth + 1));
    });
  return rows;
}

// Site-only rows for the "no organization" bucket — no aggregate row, since
// these sites share no org entity to total under.
export function buildUnassignedSiteRows(sites: { uuid: string; name: string }[]): BillingNodeRow[] {
  return [...sites]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((site) => ({
      key: `site-${site.uuid}`,
      kind: 'site' as const,
      name: site.name,
      depth: 0,
      scope: { siteUuid: site.uuid },
    }));
}
